import { Checkbox, FormControl, InputLabel, ListItemText, MenuItem, Select, SelectChangeEvent, SelectProps } from "@mui/material";

interface MultiSelectWithSelectAllProps extends Omit<SelectProps<string[]>, 'onChange' | 'value'> {
  label: string;
  value: string[];
  options: string[];
  onChange: (values: string[]) => void;
  getOptionDisabled?: (option: string) => boolean;
  getAvailableOptions?: () => string[];
}

export function MultiSelectWithSelectAll({
  label,
  value = [],
  options,
  onChange,
  getOptionDisabled,
  getAvailableOptions,
  ...selectProps
}: MultiSelectWithSelectAllProps) {
  const availableOptions = getAvailableOptions ? getAvailableOptions() : options;

  const handleSelectAll = () => {
    const allSelected = availableOptions.every(option => value.includes(option));

    if (allSelected) {
      // Desselecionar todos
      onChange([]);
    } else {
      // Selecionar todos os disponíveis
      onChange(availableOptions);
    }
  };

  const handleChange = (event: SelectChangeEvent<string[]>) => {
    const selectedValues = event.target.value as string[];

    // Verifica se "SELECT_ALL" foi clicado
    if (selectedValues.includes("SELECT_ALL")) {
      handleSelectAll();
    } else {
      onChange(selectedValues);
    }
  };

  const defaultRenderValue = (selected: unknown) => (selected as string[]).join(", ");

  const isAllSelected = availableOptions.length > 0 && availableOptions.every(option => value.includes(option));
  const isIndeterminate = value.length > 0 && !isAllSelected;

  const defaultMenuProps = {
    PaperProps: {
      style: {
        maxHeight: 300,
      },
    },
  };

  return (
    <FormControl sx={{ width: 300 }}>
      <InputLabel sx={{ color: "rgb(118, 118, 118)" }}>{label}</InputLabel>
      <Select
        multiple
        value={value}
        onChange={handleChange}
        renderValue={selectProps.renderValue || defaultRenderValue}
        MenuProps={selectProps.MenuProps || defaultMenuProps}
        sx={{
          height: "55.3px",
          marginBottom: "2.7px",
          "& .MuiOutlinedInput-notchedOutline": {
            borderRadius: "8px",
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: "rgb(121, 121, 121)",
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderWidth: "1px",
          },
        }}
        {...selectProps}
      >
        <MenuItem value="SELECT_ALL" dense>
          <Checkbox
            size="small"
            checked={isAllSelected}
            indeterminate={isIndeterminate}
            sx={{ py: 0 }}
          />
          <ListItemText
            primary="Selecionar Todos"
            primaryTypographyProps={{ fontSize: '0.875rem' }}
          />
        </MenuItem>
        {options.sort().map(option => (
          <MenuItem
            key={option}
            value={option}
            dense
            disabled={getOptionDisabled ? getOptionDisabled(option) : false}
          >
            <Checkbox
              size="small"
              checked={value.includes(option)}
              sx={{ py: 0 }}
            />
            <ListItemText
              primary={option}
              primaryTypographyProps={{ fontSize: '0.875rem' }}
            />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}
